import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Section, SerifH, Eyebrow, PrimaryButton, Bullet } from "../components/ui-kit/Primitives";
import { Sparkles, CreditCard, FileText } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";

const packages = [
  {
    name: "Tier 1: The Launchpad",
    duration: "3 Months",
    tagline: "Best for aspiring agency owners starting completely from scratch.",
    payment: { package_id: "tier1_launchpad_full", label: "Reserve — Full Payment", note: "Pay in full or in installments with Affirm" },
    items: [
      "Corporate Blueprint: Comprehensive setup guidance for your business entity, EIN acquisition, corporate banking structures, and initial National Provider Identifier (NPI) registration.",
      "Foundational Policy Framework: Custom-tailored operational policy templates mapped to state behavioral health regulations and fundamental standards.",
      "Business Credit Infrastructure: Step-by-step establishment of your business credit identity, including DUNS profile registration, LexisNexis building blocks, and your first reporting net-30 trade lines.",
      "Strategic Advisory: Bi-weekly 1-on-1 execution strategy sessions combined with priority email support to keep your setup tasks on track.",
    ],
  },
  {
    name: "Tier 2: The Optimizer & Accelerate",
    duration: "3 Months",
    tagline: "Best for operational agencies requiring optimization, compliance alignment, or credentialing access.",
    featured: true,
    payment: { package_id: "tier2_optimizer_full", label: "Reserve — Full Payment", note: "Pay in full or in installments with Affirm" },
    items: [
      "Operational Audit & Restructuring: Deep-dive evaluation of your agency's current workflows, administrative bottlenecks, and staff deployment to optimize organizational hierarchy and efficiency.",
      "Clinical Compliance & Credentialing Management: Direct, end-to-end guidance to prepare, review, and submit credentialing applications for key commercial insurance panels, Medicaid networks, or local management entities.",
      "Capital & Credit Acceleration: Implementation of a robust business credit strategy to secure advanced commercial trade lines, strategic funding profiles, or institutional business lines of credit to fund future service expansions.",
      "High-Touch Support: Weekly 1-on-1 deep-dive strategy sessions alongside direct text/email communication for swift operational problem-solving.",
    ],
  },
  {
    name: "Tier 3: The Inception-to-Launch Turnkey Engine",
    duration: "6 Months",
    tagline: "Best for ambitious, unestablished founders demanding rapid execution and an elite, ready-to-run operation.",
    payment: { package_id: "tier3_turnkey_main", label: "Reserve — $30,000 (Finance with Affirm)", note: "Finance the $30,000 core package with Affirm or pay by card." },
    secondaryPayment: { package_id: "tier3_turnkey_addon", label: "Add — $5,000 Balance", note: "Pay the remaining $5,000 separately at any time." },
    items: [
      "Phase 1: Build the Vessel (Months 1-3): Full execution of your corporate setup, comprehensive state licensing and accreditation policy manual development, site compliance checks, and Tier 1 business credit reporting accounts.",
      "Phase 2: Fire Up the Engine (Months 4-6): Fast-tracked transition into active insurance panel and Medicaid credentialing, implementation of your Electronic Health Record (EHR) configurations, and deployment of audit-ready clinical workflows.",
      "Advanced Capital Routing: Transitioning from standard vendor trade profiles to securing true commercial lending capability, line-of-credit positioning, and operational funding options.",
      "Comprehensive Continuity: Extends your engagement safely across state licensing timelines to ensure you have expert support live during the actual opening phases of your facility.",
    ],
  },
  {
    name: "Tier 4: The Elite Partner Retainer",
    duration: "6 to 12 Months",
    tagline: "Best for established agencies requiring long-term strategic partnership, continuous quality compliance, and scale.",
    payment: { package_id: "tier4_retainer_6mo_main", label: "Prepay 6 Months — $30,000 (Finance with Affirm)", note: "Finance 6 months at $30,000 with Affirm or pay by card." },
    secondaryPayment: { package_id: "tier4_retainer_6mo_balance", label: "Add — $3,000 6-Month Balance", note: "Card only. Covers the remaining $3,000 of the 6-month prepay." },
    tertiaryPayment: { package_id: "tier4_retainer_first_month", label: "Monthly — First Month ($5,500)", note: "Card only. Pay month-to-month; subsequent months invoiced." },
    items: [
      "Fractional COO Oversight: Ongoing evaluation of high-level clinical operations, system health, and strategic administrative support without the overhead of an executive-level W2 employee.",
      "Continuous Quality Improvement (CQI): Implementation of monthly or quarterly pre-emptive mock clinical audits to safeguard your agency, maintain absolute audit-readiness, and secure strict Medicaid compliance.",
      "Leadership & Culture Architecture: Dedicated coaching for agency administration and clinical directors on non-hierarchical, human-centered management practices to minimize workforce burnout and stabilize staff turnover.",
      "Ongoing Capital Optimization: Continuous positioning for high-tier lines of credit, commercial facility acquisition, or strategic public/private program expansions.",
    ],
  },
  {
    name: "Tier 5: The DIY Package",
    duration: "1-Year Contract",
    tagline: "You run the build — we hand you everything the other tiers do, plus monthly expert coaching and funding support.",
    wide: true,
    payment: {
      package_id: "tier5_diy_upfront",
      label: "Pay Upfront — $10,000 for the Year (Finance with Affirm)",
      note: "Pay in full or finance the full year with Affirm.",
    },
    secondaryPayment: {
      package_id: "invoiced",
      invoiced: true,
      label: "Pay Per Section (Invoiced)",
      note: "Prefer not to pay upfront? We invoice each section as you unlock it — contact us to set it up.",
    },
    items: [
      "Full DIY playbook: You receive every template, checklist, policy framework, and workflow from Tiers 1–3 — structured so you can execute at your own pace.",
      "Monthly 1-hour phone coaching call: Direct access to a KBX advisor each month to work through your current blockers.",
      "Monthly 1-hour Zoom working session: Screen-share walkthroughs, document reviews, and live problem-solving each month.",
      "Credit repair for up to 2 individuals, 6 months: Personal credit repair support for you and one business partner to clear the way for owner-financed credit lines.",
      "Business credit evaluation: Comprehensive assessment of your current business credit profile and a tailored plan to strengthen it.",
      "Business funding support (contingent): We help you position and apply for business funding — only pay a 7% success fee on funding you actually receive, no upfront funding fees.",
      "Full 1-year contract: Stay supported across the entire launch, structure, and credit-building journey without re-engaging.",
    ],
  },
];

const Packages = () => {
  const [loadingId, setLoadingId] = useState(null);

  const startCheckout = async (packageId) => {
    if (!packageId) return;
    setLoadingId(packageId);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: packageId,
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Could not start checkout. Please try again.");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Checkout failed.");
    } finally {
      setLoadingId(null);
    }
  };

  return (
  <div data-testid="packages-page">
    <Section className="pt-16">
      <div className="max-w-3xl">
        <Eyebrow>Work With Us</Eyebrow>
        <SerifH as="h1" className="text-5xl sm:text-6xl">
          Strategic agency-building programs — not one-off advice.
        </SerifH>
        <p className="mt-6 text-lg text-slate-600 font-light leading-relaxed">
          Each KBX package is a full engagement designed to take peer support agencies from idea to
          implementation — with structure, compliance, and growth strategy built in.
        </p>
      </div>
    </Section>

    <Section className="pt-0">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
        {packages.map((p) => (
          <div
            key={p.name}
            data-testid={`package-card-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={`relative rounded-2xl p-9 flex flex-col ${p.wide ? "md:col-span-2" : ""} ${
              p.featured
                ? "bg-brand-navy text-white border border-brand-gold/40 shadow-xl"
                : "bg-white border border-brand-green/15 shadow-sm"
            }`}
          >
            {p.featured && (
              <div className="absolute -top-3 left-9 inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase bg-brand-gold text-brand-navy rounded-full px-3 py-1.5">
                <Sparkles className="w-3 h-3" /> Most Selected
              </div>
            )}
            <div className={`text-xs tracking-[0.25em] uppercase ${p.featured ? "text-brand-gold" : "text-brand-sage"}`}>
              {p.duration}
            </div>
            <h3 className={`mt-3 font-serif text-3xl leading-tight ${p.featured ? "text-white" : "text-brand-green"}`}>
              {p.name}
            </h3>
            <p className={`mt-3 text-sm leading-relaxed ${p.featured ? "text-slate-300" : "text-slate-600"}`}>
              {p.tagline}
            </p>
            <div className={`mt-6 h-px ${p.featured ? "bg-white/15" : "bg-brand-green/10"}`} />
            <ul className="mt-6 space-y-3 flex-1">
              {p.items.map((it) => (
                <li
                  key={it}
                  className={`flex items-start gap-3 text-[15px] ${p.featured ? "text-slate-200" : "text-slate-700"}`}
                >
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-brand-gold flex-shrink-0" />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 space-y-3">
              {p.payment && (
                <>
                  <button
                    onClick={() => startCheckout(p.payment.package_id)}
                    disabled={loadingId === p.payment.package_id}
                    data-testid={`package-reserve-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={`w-full inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-medium tracking-wide transition-all disabled:opacity-60 ${
                      p.featured
                        ? "bg-brand-gold text-brand-navy hover:bg-white"
                        : "bg-brand-green text-white hover:bg-brand-navy"
                    }`}
                  >
                    <CreditCard className="w-4 h-4" />
                    {loadingId === p.payment.package_id ? "Loading…" : p.payment.label}
                  </button>
                  <p className={`text-[11px] leading-relaxed text-center ${p.featured ? "text-slate-400" : "text-slate-500"}`}>
                    {p.payment.note}
                  </p>
                </>
              )}
              {p.secondaryPayment && (
                <>
                  {p.secondaryPayment.invoiced ? (
                    <Link
                      to="/contact"
                      data-testid={`package-reserve-secondary-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className={`w-full inline-flex items-center justify-center gap-2 rounded-full px-7 py-3 text-sm font-medium tracking-wide transition-all border ${
                        p.featured
                          ? "bg-transparent text-brand-gold border-brand-gold/50 hover:bg-brand-gold/10"
                          : "bg-transparent text-brand-green border-brand-green/40 hover:bg-brand-mist"
                      }`}
                    >
                      <FileText className="w-4 h-4" />
                      {p.secondaryPayment.label}
                    </Link>
                  ) : (
                    <button
                      onClick={() => startCheckout(p.secondaryPayment.package_id)}
                      disabled={loadingId === p.secondaryPayment.package_id}
                      data-testid={`package-reserve-secondary-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className={`w-full inline-flex items-center justify-center gap-2 rounded-full px-7 py-3 text-sm font-medium tracking-wide transition-all disabled:opacity-60 border ${
                        p.featured
                          ? "bg-transparent text-brand-gold border-brand-gold/50 hover:bg-brand-gold/10"
                          : "bg-transparent text-brand-green border-brand-green/40 hover:bg-brand-mist"
                      }`}
                    >
                      <CreditCard className="w-4 h-4" />
                      {loadingId === p.secondaryPayment.package_id ? "Loading…" : p.secondaryPayment.label}
                    </button>
                  )}
                  <p className={`text-[11px] leading-relaxed text-center ${p.featured ? "text-slate-400" : "text-slate-500"}`}>
                    {p.secondaryPayment.note}
                  </p>
                </>
              )}
              {p.tertiaryPayment && (
                <>
                  <button
                    onClick={() => startCheckout(p.tertiaryPayment.package_id)}
                    disabled={loadingId === p.tertiaryPayment.package_id}
                    data-testid={`package-reserve-tertiary-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={`w-full inline-flex items-center justify-center gap-2 rounded-full px-7 py-3 text-sm font-medium tracking-wide transition-all disabled:opacity-60 border ${
                      p.featured
                        ? "bg-transparent text-brand-gold border-brand-gold/50 hover:bg-brand-gold/10"
                        : "bg-transparent text-brand-green border-brand-green/40 hover:bg-brand-mist"
                    }`}
                  >
                    <CreditCard className="w-4 h-4" />
                    {loadingId === p.tertiaryPayment.package_id ? "Loading…" : p.tertiaryPayment.label}
                  </button>
                  <p className={`text-[11px] leading-relaxed text-center ${p.featured ? "text-slate-400" : "text-slate-500"}`}>
                    {p.tertiaryPayment.note}
                  </p>
                </>
              )}
              <Link
                to="/contact"
                data-testid={`package-cta-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`w-full inline-flex items-center justify-center gap-2 rounded-full px-7 py-3 text-sm font-medium tracking-wide transition-all border ${
                  p.featured
                    ? "bg-transparent text-white border-white/30 hover:bg-white/10"
                    : "bg-transparent text-brand-green border-brand-green/40 hover:bg-brand-mist"
                }`}
              >
                Or Schedule a Strategy Call
              </Link>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-12 text-center text-sm text-slate-500 max-w-2xl mx-auto leading-relaxed">
        Every engagement is shaped around your agency's stage, scope, and timeline. Pay in full, in
        installments with Affirm, or reserve your spot with a deposit — whichever fits best.
      </div>
    </Section>

    <Section className="bg-brand-mist/60">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-8">
          <Eyebrow>Not sure which fits?</Eyebrow>
          <SerifH className="text-4xl sm:text-5xl">
            Let us help you map the right pathway for your agency.
          </SerifH>
        </div>
        <div className="lg:col-span-4 lg:text-right">
          <PrimaryButton to="/contact" testid="packages-bottom-cta">Schedule a Strategy Call</PrimaryButton>
        </div>
      </div>
    </Section>
  </div>
  );
};

export default Packages;
